import { useReducer, useEffect, useRef } from "react";
import type { CampaignFilters, SelectedCustomer } from "../types/ICampaign";
import { DEFAULT_FILTERS } from "../types/ICampaign";

interface FilterModalState {
  localFilters: CampaignFilters;
  localCustomers: SelectedCustomer[];
  emailInput: string;
  emailError: string | null;
  phoneInput: string;
  phoneError: string | null;
}

const OPPOSITE_KEYS: Partial<Record<keyof CampaignFilters, keyof CampaignFilters>> = {
  includeTags: "excludeTags",
  excludeTags: "includeTags",
  includePropertyTags: "excludePropertyTags",
  excludePropertyTags: "includePropertyTags",
  includeZipCodes: "excludeZipCodes",
  excludeZipCodes: "includeZipCodes",
  includeCities: "excludeCities",
  excludeCities: "includeCities",
  includeStates: "excludeStates",
  excludeStates: "includeStates",
  servicePlanIds: "excludeServicePlanIds",
  excludeServicePlanIds: "servicePlanIds",
  appointmentTypeIds: "excludeAppointmentTypeIds",
  excludeAppointmentTypeIds: "appointmentTypeIds",
};

type FilterModalAction =
  | { type: "INIT"; filters: CampaignFilters; customers: SelectedCustomer[] }
  | { type: "UPDATE_FILTER"; key: keyof CampaignFilters; value: CampaignFilters[keyof CampaignFilters] }
  | { type: "SET_CUSTOMERS"; customers: SelectedCustomer[] }
  | { type: "TOGGLE_ITEM"; filterKey: keyof CampaignFilters; value: number | string }
  | { type: "SET_EMAIL_INPUT"; value: string }
  | { type: "SET_EMAIL_ERROR"; error: string | null }
  | { type: "SET_PHONE_INPUT"; value: string }
  | { type: "SET_PHONE_ERROR"; error: string | null }
  | { type: "ADD_MANUAL_EMAIL"; email: string }
  | { type: "REMOVE_MANUAL_EMAIL"; email: string }
  | { type: "ADD_MANUAL_PHONE"; phone: string }
  | { type: "REMOVE_MANUAL_PHONE"; phone: string }
  | { type: "RESET" };

function createInitialState(
  filters: CampaignFilters,
  customers: SelectedCustomer[]
): FilterModalState {
  return {
    localFilters: filters,
    localCustomers: customers,
    emailInput: "",
    emailError: null,
    phoneInput: "",
    phoneError: null,
  };
}

function filterModalReducer(
  state: FilterModalState,
  action: FilterModalAction
): FilterModalState {
  switch (action.type) {
    case "INIT":
      return createInitialState(action.filters, action.customers);

    case "UPDATE_FILTER":
      return {
        ...state,
        localFilters: { ...state.localFilters, [action.key]: action.value },
      };

    case "SET_CUSTOMERS":
      return { ...state, localCustomers: action.customers };

    case "TOGGLE_ITEM": {
      const { filterKey, value } = action;
      const currentArr =
        (state.localFilters[filterKey] as (number | string)[] | null) || [];
      const isRemoving = currentArr.includes(value);
      const updated = isRemoving
        ? currentArr.filter((v) => v !== value)
        : [...currentArr, value];

      let newFilters: CampaignFilters = {
        ...state.localFilters,
        [filterKey]: updated.length > 0 ? updated : null,
      };

      // Mutual exclusivity: if item was added, remove from opposite key
      if (!isRemoving) {
        const oppositeKey = OPPOSITE_KEYS[filterKey];
        if (oppositeKey) {
          const oppArr =
            (state.localFilters[oppositeKey] as (number | string)[] | null) ||
            [];
          const oppUpdated = oppArr.filter((v) => v !== value);
          newFilters = {
            ...newFilters,
            [oppositeKey]: oppUpdated.length > 0 ? oppUpdated : null,
          };
        }
      }

      return { ...state, localFilters: newFilters };
    }

    case "SET_EMAIL_INPUT":
      return { ...state, emailInput: action.value, emailError: null };

    case "SET_EMAIL_ERROR":
      return { ...state, emailError: action.error };

    case "SET_PHONE_INPUT":
      return { ...state, phoneInput: action.value, phoneError: null };

    case "SET_PHONE_ERROR":
      return { ...state, phoneError: action.error };

    case "ADD_MANUAL_EMAIL": {
      const existing = state.localFilters.manualEmails || [];
      return {
        ...state,
        localFilters: {
          ...state.localFilters,
          manualEmails: [...existing, action.email],
        },
        emailInput: "",
        emailError: null,
      };
    }

    case "REMOVE_MANUAL_EMAIL": {
      const updated = (state.localFilters.manualEmails || []).filter(
        (e) => e !== action.email
      );
      return {
        ...state,
        localFilters: {
          ...state.localFilters,
          manualEmails: updated.length > 0 ? updated : null,
        },
      };
    }

    case "ADD_MANUAL_PHONE": {
      const existing = state.localFilters.manualPhones || [];
      return {
        ...state,
        localFilters: {
          ...state.localFilters,
          manualPhones: [...existing, action.phone],
        },
        phoneInput: "",
        phoneError: null,
      };
    }

    case "REMOVE_MANUAL_PHONE": {
      const updated = (state.localFilters.manualPhones || []).filter(
        (p) => p !== action.phone
      );
      return {
        ...state,
        localFilters: {
          ...state.localFilters,
          manualPhones: updated.length > 0 ? updated : null,
        },
      };
    }

    case "RESET":
      return {
        ...state,
        localFilters: DEFAULT_FILTERS,
        localCustomers: [],
        emailInput: "",
        emailError: null,
        phoneInput: "",
        phoneError: null,
      };

    default:
      return state;
  }
}

export function useFilterModalReducer(
  isOpen: boolean,
  filters: CampaignFilters,
  selectedCustomers: SelectedCustomer[]
) {
  const [state, dispatch] = useReducer(
    filterModalReducer,
    { filters, customers: selectedCustomers },
    (init) => createInitialState(init.filters, init.customers)
  );

  const prevOpenRef = useRef(false);

  useEffect(() => {
    // Only re-initialize when the modal transitions from closed → open.
    // This prevents prop changes (e.g., react-query refetch) from wiping
    // local edits while the user is actively interacting with the modal.
    if (isOpen && !prevOpenRef.current) {
      dispatch({ type: "INIT", filters, customers: selectedCustomers });
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, filters, selectedCustomers]);

  return [state, dispatch] as const;
}

export type { FilterModalState, FilterModalAction };
